<script setup>
import { ref, computed, onMounted } from 'vue'
import { useForm, Link, usePage, router } from '@inertiajs/vue3'
import { useI18n } from 'vue-i18n'
import ActionCluster from '@/Components/UI/ActionCluster.vue'
import AppLayout from '@/Layouts/AppLayout.vue'
import PageShell from '@/Components/UI/PageShell.vue'
import ConfirmDialog from '@/Components/UI/ConfirmDialog.vue'
import Icon from '@/Components/UI/Icon.vue'
import UiButton from '@/Components/UI/UiButton.vue'
import TimePicker from '@/Components/UI/TimePicker.vue'
import UiCard from '@/Components/UI/UiCard.vue'
import UiInput from '@/Components/UI/UiInput.vue'
import UiMenu from '@/Components/UI/UiMenu.vue'
import UiMenuItem from '@/Components/UI/UiMenuItem.vue'
import UiModal from '@/Components/UI/UiModal.vue'
import UiSelect from '@/Components/UI/UiSelect.vue'
import RentalStatusPill from '@/Components/UI/RentalStatusPill.vue'
import SignedBadge from '@/Components/UI/SignedBadge.vue'
import PaymentStatusPill from '@/Components/UI/PaymentStatusPill.vue'
import DepositStatusPill from '@/Components/UI/DepositStatusPill.vue'
import PaymentTypePill from '@/Components/UI/PaymentTypePill.vue'
import UiStatusPill from '@/Components/UI/UiStatusPill.vue'
import { useCurrency } from '@/composables/useCurrency.js'
import SignatureModal from '@/Components/SignatureModal.vue'
import ExtensionModal from '@/Components/Rentals/ExtensionModal.vue'
import LocationPickerModal from '@/Components/UI/LocationPickerModal.vue'
import { CHANNEL_CONFIG } from '@/composables/useChannelConfig.js'
import ReportDamageModal from '@/Components/Damage/ReportDamageModal.vue'
import DeliveryCard from '@/Components/Deliveries/DeliveryCard.vue'
import SendToDeliveryModal from '@/Components/Deliveries/SendToDeliveryModal.vue'
import EditDeliveryModal from '@/Components/Deliveries/EditDeliveryModal.vue'
import ReassignDriverModal from '@/Components/Deliveries/ReassignDriverModal.vue'
import LiveLocationMap from '@/Components/Deliveries/LiveLocationMap.vue'

defineOptions({ layout: AppLayout })
const { t } = useI18n()
const { formatMoney, formatDate, formatDateTime } = useCurrency()

const goBack = () => {
  if (window.history.length > 1) window.history.back()
  else router.visit('/rentals')
}

const props = defineProps({
  rental:            Object,
  deliveries:        { type: Array, default: () => [] },
  available_drivers: { type: Array, default: () => [] },
  replace_vehicles:  { type: Array, default: () => [] },
})

const page    = usePage()
const isAdmin = computed(() => page.props.auth?.user?.role === 'admin')
const canAct  = computed(() => page.props.auth?.user?.role !== 'viewer')

// ── Delivery section ───────────────────────────────────────────────────────
const showDeliveryModal = ref(false)
const deliveryKind      = ref('handover')

function openDeliveryModal(kind) {
  deliveryKind.value      = kind
  showDeliveryModal.value = true
}

function activeDelivery(kind) {
  return props.deliveries.find(
    d => d.kind === kind && ['pending', 'assigned', 'in_progress'].includes(d.status)
  )
}

function cancelDelivery(id) {
  if (!confirm('Cancel this delivery?')) return
  const reason = prompt('Reason (optional):') || ''
  router.patch(`/deliveries/${id}/cancel`, { delivery: { cancelled_reason: reason } })
}

const editingDelivery    = ref(null)
const reassigningDelivery = ref(null)
const liveMapDelivery     = ref(null)

function editDelivery(id) {
  editingDelivery.value = props.deliveries.find(d => d.id === id) || null
}

function reassignDelivery(id) {
  reassigningDelivery.value = props.deliveries.find(d => d.id === id) || null
}

function openLiveMap(id) {
  liveMapDelivery.value = props.deliveries.find(d => d.id === id) || null
}

function closeLiveMap() {
  liveMapDelivery.value = null
}

const canEditDelivery = computed(() => ['admin', 'staff'].includes(page.props.auth?.user?.role))

// ── Payment modal ──────────────────────────────────────────────────────────
const showPayment = ref(false)
const editingPaymentId = ref(null)
const reportDamageOpen = ref(false)

const paymentAmount = ref('')
const paymentForm = useForm({
  payment: {
    amount_cents: '', payment_method: 'cash', payment_type: 'final_payment',
    reference: '', notes: '', paid_at: new Date().toISOString().slice(0, 16),
    damage_id: '',
  },
})

function openPayment() {
  editingPaymentId.value = null
  paymentAmount.value = ''
  paymentForm.payment = {
    amount_cents: '', payment_method: 'cash', payment_type: 'final_payment',
    reference: '', notes: '', paid_at: new Date().toISOString().slice(0, 16),
    damage_id: '',
  }
  showPayment.value = true
}

function openPaymentEdit(p) {
  editingPaymentId.value = p.id
  paymentAmount.value = (p.amount_cents / 100).toString()
  paymentForm.payment = {
    amount_cents:   p.amount_cents,
    payment_method: p.payment_method,
    payment_type:   p.payment_type,
    reference:      p.reference || '',
    notes:          p.notes || '',
    paid_at:        p.paid_at ? new Date(p.paid_at).toISOString().slice(0, 16) : new Date().toISOString().slice(0, 16),
    damage_id:      p.damage_id || '',
  }
  showPayment.value = true
}

function closePayment() {
  showPayment.value = false
  editingPaymentId.value = null
  paymentForm.reset()
  paymentAmount.value = ''
}

const submitPayment = () => {
  const major = Number(String(paymentAmount.value).replace(',', '.'))
  paymentForm.payment.amount_cents = Number.isFinite(major) ? Math.round(major * 100) : ''
  if (editingPaymentId.value) {
    paymentForm.patch(`/rentals/${props.rental.id}/payments/${editingPaymentId.value}`, {
      onSuccess: () => closePayment(),
    })
  } else {
    paymentForm.post(`/rentals/${props.rental.id}/payments`, {
      onSuccess: () => closePayment(),
    })
  }
}

// ── Edit pending rental ────────────────────────────────────────────────────
const showEditPending = ref(false)
const editPendingForm = useForm({
  rental: {
    start_at:        '',
    expected_end_at: '',
    daily_rate:      '',
    deposit:         '',
    notes:           '',
  },
})
const dateOnly = (iso) => iso ? String(iso).slice(0, 10) : ''
const openEditPending = () => {
  editPendingForm.rental.start_at        = dateOnly(props.rental.start_at)
  editPendingForm.rental.expected_end_at = dateOnly(props.rental.expected_end_at)
  editPendingForm.rental.daily_rate      = ((props.rental.daily_rate_cents || 0) / 100).toString()
  editPendingForm.rental.deposit         = ((props.rental.required_deposit_cents || 0) / 100).toString()
  editPendingForm.rental.notes           = props.rental.notes || ''
  showEditPending.value = true
}
const submitEditPending = () => {
  const major = (v) => Number(String(v).replace(',', '.'))
  const rate = major(editPendingForm.rental.daily_rate)
  const dep  = major(editPendingForm.rental.deposit)
  const payload = {
    rental: {
      start_at:           editPendingForm.rental.start_at,
      expected_end_at:    editPendingForm.rental.expected_end_at,
      daily_rate_cents:   Number.isFinite(rate) ? Math.round(rate * 100) : 0,
      deposit_paid_cents: Number.isFinite(dep) ? Math.round(dep * 100) : 0,
      notes:              editPendingForm.rental.notes,
    },
  }
  router.patch(`/rentals/${props.rental.id}`, payload, {
    preserveScroll: true,
    onSuccess: () => { showEditPending.value = false },
  })
}

// ── Payment delete ─────────────────────────────────────────────────────────
const paymentToDelete = ref(null)
const askDeletePayment = (p) => { paymentToDelete.value = p }
const confirmDeletePayment = () => {
  if (!paymentToDelete.value) return
  router.delete(`/rentals/${props.rental.id}/payments/${paymentToDelete.value.id}`, {
    preserveScroll: true,
    onFinish: () => { paymentToDelete.value = null },
  })
}

const paymentTypeOptions = computed(() => [
  { value: 'deposit', label: t('rental.show.paymentModal.types.deposit') },
  { value: 'partial_payment', label: t('rental.show.paymentModal.types.partial_payment') },
  { value: 'final_payment', label: t('rental.show.paymentModal.types.final_payment') },
  { value: 'extra_charge', label: t('rental.show.paymentModal.types.extra_charge') },
  { value: 'refund', label: t('rental.show.paymentModal.types.refund') },
])

const paymentMethodOptions = computed(() => [
  { value: 'cash', label: t('rental.show.paymentModal.methods.cash') },
  { value: 'card', label: t('rental.show.paymentModal.methods.card') },
  { value: 'bank_transfer', label: t('rental.show.paymentModal.methods.bank_transfer') },
  { value: 'online', label: t('rental.show.paymentModal.methods.online') },
])

const damageOptions = computed(() => [
  { value: '', label: t('damage.noneOption') },
  ...((props.rental.open_damages || []).map(d => ({
    value: d.id,
    label: `${t(`damage.severity.${d.severity}`)} — ${d.description.slice(0, 50)} (${formatMoney(d.net_cost_cents)})`,
  }))),
])

// ── Event log helpers ──────────────────────────────────────────────────────
const EVENT_COLORS = {
  created:              'var(--muted)',
  started:              'var(--ok)',
  finished:             'var(--accent)',
  cancelled:            'var(--danger)',
  vehicle_replaced:     'var(--warn)',
  written_off:          'var(--danger)',
  edited:               'var(--muted)',
  extended:             'var(--accent)',
  delivery_created:     'var(--accent)',
  delivery_claimed:     'var(--accent)',
  delivery_in_progress: 'var(--warn)',
  delivery_completed:   'var(--ok)',
  delivery_cancelled:   'var(--muted)',
  delivery_reassigned:  'var(--accent)',
  delivery_updated:     'var(--accent)',
}
const eventDotColor = (kind) => EVENT_COLORS[kind] || 'var(--muted)'
const eventLabel = (e) => {
  const p = e.payload || {}
  if (e.kind === 'extended' && p.new_end_at) {
    return t('rental.show.events.extended', { date: formatDateTime(p.new_end_at), days: p.days_added })
  }
  return t(`rental.show.events.${e.kind}`)
}
const eventDetail = (e) => {
  const p = e.payload || {}
  if (e.kind === 'vehicle_replaced' && p.from && p.to) {
    return `${p.from.label} (${p.from.reg}) → ${p.to.label} (${p.to.reg})`
  }
  if (e.kind === 'written_off' && p.amount_cents != null) {
    return formatMoney(p.amount_cents)
  }
  if (e.kind === 'cancelled' && p.reason) {
    return p.reason
  }
  if (e.kind === 'finished' && p.extra_charges_cents > 0) {
    return `${t('rental.show.extras')}: ${formatMoney(p.extra_charges_cents)}`
  }
  return null
}

// ── Start rental modal ─────────────────────────────────────────────────────
const showStart = ref(false)
const isHHMM = (v) => typeof v === 'string' && /^\d{2}:\d{2}$/.test(v)
const combineDateTime = (iso, timeOverride) => {
  if (!iso) return ''
  const datePart = iso.slice(0, 10)
  const timePart = isHHMM(timeOverride) ? timeOverride : iso.slice(11, 16)
  return `${datePart}T${timePart}`
}
const startForm = useForm({
  rental: {
    start_at:        combineDateTime(props.rental.start_at, props.rental.handover_time),
    expected_end_at: combineDateTime(props.rental.expected_end_at, props.rental.return_time),
  },
  as_planned: true,
})
function openStart() {
  startForm.rental.start_at        = combineDateTime(props.rental.start_at, props.rental.handover_time)
  startForm.rental.expected_end_at = combineDateTime(props.rental.expected_end_at, props.rental.return_time)
  startForm.as_planned = true
  showStart.value = true
}
function submitStart() {
  const payload = startForm.as_planned
    ? { rental: {} }
    : { rental: {
        start_at:        startForm.rental.start_at,
        expected_end_at: startForm.rental.expected_end_at,
      } }
  router.patch(`/rentals/${props.rental.id}/start`, payload, {
    onSuccess: () => { showStart.value = false },
  })
}

// ── Extension modal ────────────────────────────────────────────────────────
const showExtension = ref(false)
const extensionConflict = computed(() => {
  const f = page.props?.flash?.extension_conflict
  if (!f) return null
  return { id: f.conflict_id, start: formatDateTime(f.conflict_start_at), customer: f.conflict_customer }
})

function openExtension() { showExtension.value = true }
function closeExtension() { showExtension.value = false }

// Auto-open the modal pre-flagged with the conflict warning when the
// controller redirected back here with flash[:extension_conflict].
onMounted(() => {
  if (extensionConflict.value) showExtension.value = true
})

const eSignExtensionId = ref(null)
function openExtensionSign(id) { eSignExtensionId.value = id }
function closeExtensionSign() { eSignExtensionId.value = null }
function onExtensionSigned() {
  closeExtensionSign()
  router.reload({ only: ['rental'] })
}

// ── Cancel modal ───────────────────────────────────────────────────────────
const showCancel = ref(false)
const cancelConsequence = computed(() => {
  const refund = props.rental.cancel_refund_cents
  if (refund > 0) return t('rental.cancel.consequence', { amount: formatMoney(refund), customer: props.rental.customer_name, method: props.rental.last_payment_method || 'cash' })
  return t('rental.cancel.noRefund')
})
const cancelForm = useForm({})
const submitCancel = () => cancelForm.patch(`/rentals/${props.rental.id}/cancel`, {
  onSuccess: () => { showCancel.value = false },
})

// ── Write-off modal ────────────────────────────────────────────────────────
const showWriteOff = ref(false)
const writeOffConsequence = computed(() =>
  t('rental.writeOff.consequence', { amount: formatMoney(props.rental.balance_due_cents), customer: props.rental.customer_name })
)
const writeOffForm = useForm({})
const submitWriteOff = () => writeOffForm.patch(`/rentals/${props.rental.id}/write_off`, {
  onSuccess: () => { showWriteOff.value = false },
})

// ── Replace-vehicle modal ──────────────────────────────────────────────────
const showReplace = ref(false)
const replaceForm = useForm({ vehicle_id: null })
const canReplace = computed(() =>
  canAct.value && props.rental.status === 'active' && props.replace_vehicles.length > 0
)
const submitReplace = () => {
  if (!replaceForm.vehicle_id) return
  replaceForm.patch(`/rentals/${props.rental.id}/replace_vehicle`, {
    preserveScroll: true,
    onSuccess: () => { showReplace.value = false; replaceForm.reset() },
  })
}

// ── Handover point ─────────────────────────────────────────────────────────
const showHandover = ref(false)
const handoverSaving = ref(false)
const onHandoverSave = ({ address, lat, lng }) => {
  handoverSaving.value = true
  router.patch(`/rentals/${props.rental.id}/handover`, {
    rental: { handover_address: address, handover_lat: lat, handover_lng: lng },
  }, {
    preserveScroll: true,
    onFinish: () => { handoverSaving.value = false; showHandover.value = false },
  })
}
const showResetHandover = ref(false)
const confirmResetHandover = () => {
  showResetHandover.value = false
  router.patch(`/rentals/${props.rental.id}/handover`, {
    rental: { handover_address: '', handover_lat: '', handover_lng: '' },
  }, { preserveScroll: true })
}

// ── Handover time (inline editor) ──────────────────────────────────────────
const editingHandoverTime = ref(false)
const handoverTimeDraft = ref(props.rental.handover_time || '')
const openHandoverTime = () => {
  handoverTimeDraft.value = props.rental.handover_time || ''
  editingHandoverTime.value = true
}
const saveHandoverTime = () => {
  router.patch(`/rentals/${props.rental.id}/handover`, {
    rental: { handover_time: handoverTimeDraft.value || '' },
  }, {
    preserveScroll: true,
    onSuccess: () => { editingHandoverTime.value = false },
  })
}
const clearHandoverTime = () => {
  router.patch(`/rentals/${props.rental.id}/handover`, {
    rental: { handover_time: '' },
  }, {
    preserveScroll: true,
    onSuccess: () => { editingHandoverTime.value = false },
  })
}

// ── Return point ───────────────────────────────────────────────────────────
const showReturn = ref(false)
const returnSaving = ref(false)
const onReturnSave = ({ address, lat, lng }) => {
  returnSaving.value = true
  router.patch(`/rentals/${props.rental.id}/handover`, {
    rental: { return_address: address, return_lat: lat, return_lng: lng },
  }, {
    preserveScroll: true,
    onFinish: () => { returnSaving.value = false; showReturn.value = false },
  })
}
const showResetReturn = ref(false)
const confirmResetReturn = () => {
  showResetReturn.value = false
  router.patch(`/rentals/${props.rental.id}/handover`, {
    rental: { return_address: '', return_lat: '', return_lng: '' },
  }, { preserveScroll: true })
}

// ── Return time (inline editor) ────────────────────────────────────────────
const editingReturnTime = ref(false)
const returnTimeDraft = ref(props.rental.return_time || '')
const openReturnTime = () => {
  returnTimeDraft.value = props.rental.return_time || ''
  editingReturnTime.value = true
}
const saveReturnTime = () => {
  router.patch(`/rentals/${props.rental.id}/handover`, {
    rental: { return_time: returnTimeDraft.value || '' },
  }, {
    preserveScroll: true,
    onSuccess: () => { editingReturnTime.value = false },
  })
}
const clearReturnTime = () => {
  router.patch(`/rentals/${props.rental.id}/handover`, {
    rental: { return_time: '' },
  }, {
    preserveScroll: true,
    onSuccess: () => { editingReturnTime.value = false },
  })
}

// ── Contract gate ──────────────────────────────────────────────────────────
const contractBlocked = computed(() => (props.rental.contract_blockers || []).length > 0)

// ── Agreement ─────────────────────────────────────────────────────────────
const showSignModal = ref(false)

const onSigned = () => {
  showSignModal.value = false
  router.reload({ only: ['rental'] })
}

const printUrl = (signed) => signed ? props.rental.signed_agreement_path : props.rental.agreement_path
const shareUrl = (signed) => window.location.origin + (signed ? props.rental.signed_agreement_path : props.rental.agreement_path)

const shareLink = (channel, signed) => {
  const url  = shareUrl(signed)
  const text = encodeURIComponent(`Here is your rental agreement: ${url}`)
  if (channel.platform === 'whatsapp') return `https://wa.me/${channel.handle.replace(/\D/g, '')}?text=${text}`
  if (channel.platform === 'telegram') return `https://t.me/${channel.handle.replace('@', '')}?text=${text}`
  if (channel.platform === 'line')     return `https://line.me/R/msg/text/?${text}`
  const cfg = CHANNEL_CONFIG[channel.platform]
  return cfg ? cfg.deepLink(channel.handle) : '#'
}

// ── Send-for-signing ──────────────────────────────────────────────────────
const showRevokeConfirm = ref(false)

const sendSigningLink = (via) => {
  const body = new FormData()
  body.append('via', via)
  if (via === 'inbox' && props.rental.chat_conversation_id) {
    body.append('conversation_id', String(props.rental.chat_conversation_id))
  }
  router.post(`/rentals/${props.rental.id}/signing_link`, body, { preserveScroll: true })
}

const copySigningUrl = async () => {
  if (!props.rental.signing_url) return
  await navigator.clipboard.writeText(props.rental.signing_url)
}

const revokeSigningLink = () => {
  router.delete(`/rentals/${props.rental.id}/signing_link`, { preserveScroll: true })
  showRevokeConfirm.value = false
}

const formatExpiry = (raw) => raw ? new Date(raw).toLocaleString() : ''

const changeOffice = (officeId) => {
  router.patch(`/rentals/${props.rental.id}`, {
    rental: { office_id: officeId },
  }, { preserveScroll: true })
}

// ── Header action cluster ──────────────────────────────────────────────────
const headerActions = computed(() => {
  if (!canAct.value) return []
  const a = []
  a.push({
    key: 'esign',
    label: props.rental.signed ? t('rental.show.resign') : t('rental.show.esign'),
    icon: 'sign',
    variant: 'primary',
    onClick: () => { showSignModal.value = true },
    disabled: !props.rental.saved_contracts?.length,
    primary: true,
  })
  if (props.rental.chat_conversation_id) {
    a.push({
      key: 'chat',
      label: t('rental.show.chat'),
      icon: 'chat',
      onClick: () => router.visit(`/inbox/conversations/${props.rental.chat_conversation_id}`),
    })
  }
  a.push({
    key: 'gps',
    label: t('rental.show.gps'),
    icon: 'pin',
    onClick: () => router.visit(`/rentals/${props.rental.id}/gps`),
  })
  return a
})

const canCancel  = computed(() => ['pending', 'active'].includes(props.rental.status))
const canWriteOff = computed(() =>
  ['active', 'returned'].includes(props.rental.status)
  && props.rental.balance_due_cents > 0
  && isAdmin.value
  && !props.rental.written_off_at,
)

</script>

<template>
  <PageShell :title="t('nav.rentals')">
    <div class="flex items-center justify-between mb-4 gap-3 flex-wrap">
      <div class="flex items-center gap-3 min-w-0">
        <button
          type="button"
          :aria-label="t('common.back')"
          class="inline-flex items-center text-[color:var(--muted)] hover:text-[color:var(--ink)] transition-colors"
          @click="goBack"
        >
          <Icon
            name="chev-l"
            :size="18"
          />
        </button>
        <Link
          :href="`/customers/${rental.customer_id}`"
          class="text-xl font-semibold text-[color:var(--ink)] hover:text-[color:var(--accent)] truncate"
        >
          {{ rental.customer_name }}
        </Link>
        <span class="text-sm text-[color:var(--muted)] font-mono">#{{ rental.id }}</span>
        <span
          v-if="rental.days_since_start != null"
          class="text-xs text-[color:var(--muted)] hidden sm:block"
        >
          {{ t('rental.show.startedAgo', { duration: rental.days_since_start + 'd' }) }}
        </span>
      </div>

      <div class="flex items-center gap-2 flex-shrink-0 flex-wrap">
        <ActionCluster :actions="headerActions" />

        <!-- Send for signing -->
        <div
          v-if="rental.has_contract && canAct"
          class="hidden md:block"
        >
          <UiMenu align="right">
            <template #trigger="{ toggle }">
              <UiButton
                variant="secondary"
                size="sm"
                :disabled="contractBlocked || rental.signing_status === 'awaiting'"
                icon-right="chev-d"
                @click="rental.has_contract && rental.signing_status !== 'awaiting' && toggle()"
              >
                {{ t('rental.show.actions.sendForSigning') }}
              </UiButton>
            </template>
            <UiMenuItem
              v-if="rental.chat_conversation_id"
              @click="sendSigningLink('inbox')"
            >
              {{ t('rental.signing.deliveryInbox') }}
            </UiMenuItem>
            <UiMenuItem @click="sendSigningLink('email')">
              {{ t('rental.signing.deliveryEmail') }}
            </UiMenuItem>
          </UiMenu>
        </div>

        <!-- Print menu: hidden on mobile (use Documents card to download) -->
        <div class="hidden md:block">
          <UiMenu align="right">
            <template #trigger="{ toggle }">
              <UiButton
                variant="secondary"
                size="sm"
                :disabled="!rental.saved_contracts?.length"
                icon-left="print"
                icon-right="chev-d"
                @click="rental.saved_contracts?.length && toggle()"
              >
                {{ t('rental.show.print') }}
              </UiButton>
            </template>
            <UiMenuItem
              :href="printUrl(false)"
              target="_blank"
            >
              {{ t('rental.show.original') }}
            </UiMenuItem>
            <UiMenuItem
              :href="rental.signed ? printUrl(true) : null"
              target="_blank"
              :disabled="!rental.signed"
            >
              {{ t('rental.show.eSigned') }}
            </UiMenuItem>
          </UiMenu>
        </div>

        <!-- Share menu: hidden on mobile -->
        <div
          v-if="rental.customer_channels?.length"
          class="hidden md:block"
        >
          <UiMenu align="right">
            <template #trigger="{ toggle }">
              <UiButton
                variant="secondary"
                size="sm"
                :disabled="!rental.saved_contracts?.length"
                icon-left="share"
                icon-right="chev-d"
                @click="rental.saved_contracts?.length && toggle()"
              >
                {{ t('rental.show.share') }}
              </UiButton>
            </template>
            <p class="px-4 py-1.5 text-xs font-semibold uppercase tracking-wider text-[color:var(--muted)]">
              {{ t('rental.show.original') }}
            </p>
            <UiMenuItem
              v-for="ch in rental.customer_channels"
              :key="ch.platform + '-orig'"
              :href="shareLink(ch, false)"
              target="_blank"
              class="capitalize"
            >
              {{ CHANNEL_CONFIG[ch.platform]?.label || ch.platform }}
            </UiMenuItem>
            <hr class="my-1 border-[color:var(--line)]">
            <p
              class="px-4 py-1.5 text-xs font-semibold uppercase tracking-wider text-[color:var(--muted)]"
              :class="rental.signed ? '' : 'opacity-50'"
            >
              {{ t('rental.show.eSigned') }}
            </p>
            <template v-if="rental.signed">
              <UiMenuItem
                v-for="ch in rental.customer_channels"
                :key="ch.platform + '-signed'"
                :href="shareLink(ch, true)"
                target="_blank"
                class="capitalize"
              >
                {{ CHANNEL_CONFIG[ch.platform]?.label || ch.platform }}
              </UiMenuItem>
            </template>
          </UiMenu>
        </div>
      </div>
    </div>

    <!-- Signing status row -->
    <div
      v-if="rental.signing_status === 'awaiting'"
      class="mb-2 flex flex-wrap items-center gap-2 text-xs text-[color:var(--muted)]"
    >
      <span>{{ t('rental.signing.awaiting') }}</span>
      <span>·</span>
      <span>{{ t('rental.signing.expiresIn', { when: formatExpiry(rental.signing_expires_at) }) }}</span>
      <button
        type="button"
        class="underline hover:text-[color:var(--ink)]"
        @click="sendSigningLink('email')"
      >
        {{ t('rental.show.actions.resendLink') }}
      </button>
      <button
        type="button"
        class="underline hover:text-[color:var(--ink)]"
        @click="copySigningUrl"
      >
        {{ t('rental.show.actions.copyLink') }}
      </button>
      <button
        type="button"
        class="underline text-[color:var(--danger)] hover:opacity-80"
        @click="showRevokeConfirm = true"
      >
        {{ t('rental.show.actions.revokeLink') }}
      </button>
    </div>
    <p
      v-else-if="rental.signing_status === 'signed' && rental.signed_at"
      class="mb-2 text-xs text-[color:var(--muted)]"
    >
      <template v-if="rental.signed_via === 'link'">
        {{ t('rental.signing.signedRemotely', { when: formatExpiry(rental.signed_at) }) }}
      </template>
      <template v-else>
        {{ t('rental.signing.signedInPerson', { when: formatExpiry(rental.signed_at) }) }}
      </template>
    </p>

    <ConfirmDialog
      v-if="showRevokeConfirm"
      :open="showRevokeConfirm"
      :title="t('rental.show.actions.revokeLink')"
      :consequence="t('rental.signing.revokeConfirm')"
      danger
      @confirm="revokeSigningLink"
      @cancel="showRevokeConfirm = false"
    />

    <!-- Body -->
    <div class="grid grid-cols-1 xl:grid-cols-5 gap-6">
      <!-- Left: 60% -->
      <div class="xl:col-span-3 space-y-4">
        <!-- Rental details card -->
        <UiCard :title="t('rental.show.details')">
          <template #actions>
            <RentalStatusPill
              :status="rental.status"
              :overdue="rental.overdue"
            />
            <SignedBadge v-if="rental.signed" />
            <PaymentStatusPill
              v-if="rental.status !== 'cancelled'"
              :status="rental.payment_status"
            />
            <DepositStatusPill
              v-if="rental.status !== 'cancelled' && rental.required_deposit_cents > 0"
              :status="rental.deposit_status"
            />
            <span
              v-if="rental.rate_plan_id"
              class="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium"
              :style="{ background: 'var(--accentSoft)', color: 'var(--accent)' }"
            >
              {{ t('rental.show.fields.bookedUnder', { plan: rental.rate_plan_name }) }}
            </span>
          </template>
          <dl class="grid grid-cols-1 md:grid-cols-2 gap-x-6 gap-y-3 text-sm">
            <div>
              <dt class="text-[color:var(--muted)]">
                {{ t('rental.show.fields.customer') }}
              </dt>
              <dd class="fw-value mt-0.5">
                <Link
                  :href="`/customers/${rental.customer_id}`"
                  class="text-[color:var(--accent)] hover:underline"
                >
                  {{ rental.customer_name }}
                </Link>
              </dd>
            </div>
            <div>
              <dt class="text-[color:var(--muted)]">
                {{ t('rental.show.fields.vehicle') }}
              </dt>
              <dd class="fw-value mt-0.5">
                <Link
                  :href="`/vehicles/${rental.vehicle_id}`"
                  class="text-[color:var(--accent)] hover:underline"
                >
                  {{ rental.vehicle_name }}
                  <span class="text-[color:var(--muted)] ml-1 font-normal">{{ rental.vehicle_reg }}</span>
                </Link>
              </dd>
            </div>
            <div>
              <dt class="text-[color:var(--muted)]">
                {{ t('rental.show.fields.start') }}
              </dt>
              <dd class="fw-value text-[color:var(--ink)] mt-0.5">
                {{ formatDate(rental.start_at) }}
              </dd>
            </div>
            <div v-if="rental.actual_start_at">
              <dt class="text-[color:var(--muted)]">
                {{ t('rental.show.fields.actualStart') }}
              </dt>
              <dd class="fw-value text-[color:var(--ink)] mt-0.5">
                {{ formatDateTime(rental.actual_start_at) }}
              </dd>
            </div>
            <div>
              <dt class="text-[color:var(--muted)]">
                {{ t('rental.show.fields.expectedReturn') }}
              </dt>
              <dd
                class="fw-value mt-0.5"
                :class="rental.overdue ? 'text-[color:var(--danger)]' : 'text-[color:var(--ink)]'"
              >
                {{ formatDate(rental.expected_end_at) }}
              </dd>
            </div>
            <div v-if="rental.actual_end_at">
              <dt class="text-[color:var(--muted)]">
                {{ t('rental.show.fields.actualReturn') }}
              </dt>
              <dd class="fw-value text-[color:var(--ink)] mt-0.5">
                {{ formatDateTime(rental.actual_end_at) }}
              </dd>
            </div>
            <div>
              <dt class="text-[color:var(--muted)]">
                {{ t('rental.show.fields.dailyRate') }}
              </dt>
              <dd class="fw-value text-[color:var(--ink)] mt-0.5">
                {{ formatMoney(rental.daily_rate_cents) }}
              </dd>
            </div>
            <div>
              <dt class="text-[color:var(--muted)]">
                {{ t('rental.show.fields.depositPaid') }}
              </dt>
              <dd class="flex items-center gap-1.5 flex-wrap mt-0.5">
                <span
                  class="fw-value"
                  :class="rental.deposit_status === 'paid' ? 'text-[color:var(--ink)]' : 'text-[color:var(--warn)]'"
                >
                  {{ formatMoney(rental.deposit_paid_cents) }}
                  <template v-if="rental.required_deposit_cents > 0"> / {{ formatMoney(rental.required_deposit_cents) }}</template>
                </span>
                <DepositStatusPill :status="rental.deposit_status" />
              </dd>
            </div>
            <div class="md:col-span-2 pt-2 mt-1 border-t border-[color:var(--line)]">
              <dt class="text-[color:var(--muted)]">
                {{ t('rental.show.fields.office') }}
              </dt>
              <dd class="fw-value mt-0.5 text-[color:var(--ink)] flex items-center gap-2 flex-wrap">
                <span>{{ rental.office_name || '—' }}</span>
                <UiMenu
                  v-if="canAct && rental.offices?.length > 1"
                  align="left"
                >
                  <template #trigger="{ toggle }">
                    <button
                      type="button"
                      class="text-xs text-[color:var(--accent)] underline"
                      @click="toggle"
                    >
                      {{ t('rental.show.changeOffice') }}
                    </button>
                  </template>
                  <UiMenuItem
                    v-for="o in rental.offices"
                    :key="o.id"
                    :disabled="o.id === rental.office_id"
                    @click="changeOffice(o.id)"
                  >
                    {{ o.name }}
                    <span
                      v-if="o.id === rental.office_id"
                      class="text-xs text-[color:var(--muted)] ml-1"
                    >· {{ t('offices.current') }}</span>
                  </UiMenuItem>
                </UiMenu>
              </dd>
            </div>
            <div>
              <dt class="text-[color:var(--muted)]">
                {{ t('rental.show.fields.handoverPoint') }}
              </dt>
              <dd class="flex items-start gap-2 mt-0.5">
                <div class="flex-1 min-w-0 flex items-center gap-2 flex-wrap">
                  <span
                    v-if="rental.handover_address"
                    class="fw-value text-[color:var(--ink)]"
                  >{{ rental.handover_address }}</span>
                  <template v-else-if="rental.office_address">
                    <span class="fw-value text-[color:var(--ink)]">{{ rental.office_address }}</span>
                    <span
                      class="px-1.5 py-0.5 rounded-full text-xs"
                      :style="{ background: 'var(--surfaceAlt)', color: 'var(--muted)' }"
                    >
                      {{ t('rental.show.fields.handoverDefault') }}
                    </span>
                  </template>
                  <span
                    v-else
                    class="text-[color:var(--muted)]"
                  >{{ t('rental.show.fields.handoverNotSet') }}</span>
                </div>
                <div
                  v-if="canAct"
                  class="flex items-center gap-3 shrink-0"
                >
                  <button
                    type="button"
                    class="text-[color:var(--accent)] hover:underline text-sm"
                    @click="showHandover = true"
                  >
                    {{ rental.handover_address ? t('common.edit') : t('rental.show.setHandover') }}
                  </button>
                  <button
                    v-if="rental.handover_address && rental.office_address"
                    type="button"
                    class="text-[color:var(--muted)] hover:text-[color:var(--ink)] hover:underline text-sm"
                    @click="showResetHandover = true"
                  >
                    {{ t('rental.show.resetHandover') }}
                  </button>
                </div>
              </dd>
            </div>
            <div>
              <dt class="text-[color:var(--muted)]">
                {{ t('rental.show.fields.handoverTime') }}
              </dt>
              <dd class="flex items-center gap-2 mt-0.5">
                <template v-if="!editingHandoverTime">
                  <span
                    v-if="rental.handover_time"
                    class="fw-value text-[color:var(--ink)] flex-1"
                  >{{ rental.handover_time }}</span>
                  <span
                    v-else
                    class="text-[color:var(--muted)] flex-1"
                  >{{ t('rental.show.fields.handoverNotSet') }}</span>
                  <div
                    v-if="canAct"
                    class="flex items-center gap-3 shrink-0"
                  >
                    <button
                      type="button"
                      class="text-[color:var(--accent)] hover:underline text-sm"
                      @click="openHandoverTime"
                    >
                      {{ rental.handover_time ? t('common.edit') : t('rental.show.setHandoverTime') }}
                    </button>
                    <button
                      v-if="rental.handover_time"
                      type="button"
                      class="text-[color:var(--muted)] hover:text-[color:var(--ink)] hover:underline text-sm"
                      @click="clearHandoverTime"
                    >
                      {{ t('common.clear') }}
                    </button>
                  </div>
                </template>
                <template v-else>
                  <TimePicker v-model="handoverTimeDraft" />
                  <UiButton
                    type="button"
                    variant="primary"
                    size="sm"
                    @click="saveHandoverTime"
                  >
                    {{ t('common.save') }}
                  </UiButton>
                  <UiButton
                    type="button"
                    variant="ghost"
                    size="sm"
                    @click="editingHandoverTime = false"
                  >
                    {{ t('common.cancel') }}
                  </UiButton>
                </template>
              </dd>
            </div>

            <!-- Return point -->
            <div>
              <dt class="text-[color:var(--muted)]">
                {{ t('rental.show.fields.returnPoint') }}
              </dt>
              <dd class="flex items-start gap-2 mt-0.5">
                <div class="flex-1 min-w-0 flex items-center gap-2 flex-wrap">
                  <span
                    v-if="rental.return_address"
                    class="fw-value text-[color:var(--ink)]"
                  >{{ rental.return_address }}</span>
                  <template v-else-if="rental.office_address">
                    <span class="fw-value text-[color:var(--ink)]">{{ rental.office_address }}</span>
                    <span
                      class="px-1.5 py-0.5 rounded-full text-xs"
                      :style="{ background: 'var(--surfaceAlt)', color: 'var(--muted)' }"
                    >
                      {{ t('rental.show.fields.handoverDefault') }}
                    </span>
                  </template>
                  <span
                    v-else
                    class="text-[color:var(--muted)]"
                  >{{ t('rental.show.fields.handoverNotSet') }}</span>
                </div>
                <div
                  v-if="canAct"
                  class="flex items-center gap-3 shrink-0"
                >
                  <button
                    type="button"
                    class="text-[color:var(--accent)] hover:underline text-sm"
                    @click="showReturn = true"
                  >
                    {{ rental.return_address ? t('common.edit') : t('rental.show.setHandover') }}
                  </button>
                  <button
                    v-if="rental.return_address && rental.office_address"
                    type="button"
                    class="text-[color:var(--muted)] hover:text-[color:var(--ink)] hover:underline text-sm"
                    @click="showResetReturn = true"
                  >
                    {{ t('rental.show.resetHandover') }}
                  </button>
                </div>
              </dd>
            </div>

            <!-- Return time -->
            <div>
              <dt class="text-[color:var(--muted)]">
                {{ t('rental.show.fields.returnTime') }}
              </dt>
              <dd class="flex items-center gap-2 mt-0.5">
                <template v-if="!editingReturnTime">
                  <span
                    v-if="rental.return_time"
                    class="fw-value text-[color:var(--ink)] flex-1"
                  >{{ rental.return_time }}</span>
                  <span
                    v-else
                    class="text-[color:var(--muted)] flex-1"
                  >{{ t('rental.show.fields.handoverNotSet') }}</span>
                  <div
                    v-if="canAct"
                    class="flex items-center gap-3 shrink-0"
                  >
                    <button
                      type="button"
                      class="text-[color:var(--accent)] hover:underline text-sm"
                      @click="openReturnTime"
                    >
                      {{ rental.return_time ? t('common.edit') : t('rental.show.setHandoverTime') }}
                    </button>
                    <button
                      v-if="rental.return_time"
                      type="button"
                      class="text-[color:var(--muted)] hover:text-[color:var(--ink)] hover:underline text-sm"
                      @click="clearReturnTime"
                    >
                      {{ t('common.clear') }}
                    </button>
                  </div>
                </template>
                <template v-else>
                  <TimePicker v-model="returnTimeDraft" />
                  <UiButton
                    type="button"
                    variant="primary"
                    size="sm"
                    @click="saveReturnTime"
                  >
                    {{ t('common.save') }}
                  </UiButton>
                  <UiButton
                    type="button"
                    variant="ghost"
                    size="sm"
                    @click="editingReturnTime = false"
                  >
                    {{ t('common.cancel') }}
                  </UiButton>
                </template>
              </dd>
            </div>

            <div v-if="rental.extra_charges_cents > 0">
              <dt class="text-[color:var(--muted)]">
                {{ t('rental.show.fields.extraCharges') }}
              </dt>
              <dd class="fw-value text-[color:var(--ink)] mt-0.5">
                {{ formatMoney(rental.extra_charges_cents) }}
              </dd>
            </div>
            <div
              v-if="rental.notes"
              class="md:col-span-2"
            >
              <dt class="text-[color:var(--muted)]">
                {{ t('rental.show.fields.notes') }}
              </dt>
              <dd class="text-[color:var(--inkSoft)] mt-0.5">
                {{ rental.notes }}
              </dd>
            </div>
          </dl>

          <!-- Financial totals -->
          <div class="mt-4 pt-4 border-t border-[color:var(--line)] space-y-1.5">
            <div class="flex justify-between text-sm">
              <span class="text-[color:var(--inkSoft)]">{{ t('rental.show.subtotal', { days: rental.days_rented, rate: formatMoney(rental.daily_rate_cents) }) }}</span>
              <span class="tabular-nums text-[color:var(--ink)]">{{ formatMoney(rental.days_rented * rental.daily_rate_cents) }}</span>
            </div>
            <div
              v-if="rental.extra_charges_cents > 0"
              class="flex justify-between text-sm"
            >
              <span class="text-[color:var(--inkSoft)]">{{ t('rental.show.fields.extraCharges') }}</span>
              <span class="tabular-nums text-[color:var(--ink)]">{{ formatMoney(rental.extra_charges_cents) }}</span>
            </div>
            <div class="flex justify-between text-base fw-value pt-1 border-t border-[color:var(--line)] text-[color:var(--ink)]">
              <span>{{ t('rental.show.fields.finalTotal') }}</span>
              <span class="tabular-nums">{{ formatMoney(rental.final_total_cents) }}</span>
            </div>
            <div
              class="flex justify-between text-sm"
              :class="rental.deposit_status === 'paid' ? 'text-[color:var(--ok)]' : 'text-[color:var(--warn)]'"
            >
              <span class="flex items-center gap-1">
                {{ t('rental.show.fields.depositPaid') }}
                <Icon
                  v-if="rental.deposit_status !== 'paid'"
                  name="alert"
                  :size="14"
                />
                <span
                  v-if="rental.deposit_status === 'paid'"
                  class="text-xs font-medium"
                >({{ t('rental.show.fields.depositStatus.paid') }})</span>
                <span
                  v-else-if="rental.deposit_status === 'partial'"
                  class="text-xs font-medium"
                >({{ t('rental.show.fields.depositStatus.partial') }})</span>
                <span
                  v-else-if="rental.deposit_status === 'none'"
                  class="text-xs font-medium"
                >({{ t('rental.show.fields.depositStatus.none') }})</span>
              </span>
              <span class="tabular-nums">
                {{ formatMoney(rental.deposit_paid_cents) }}
                <template v-if="rental.required_deposit_cents > 0 && rental.deposit_status !== 'paid'"> / {{ formatMoney(rental.required_deposit_cents) }}</template>
              </span>
            </div>
            <div class="flex justify-between text-sm text-[color:var(--ok)]">
              <span>{{ t('rental.show.fields.amountPaid') }}</span>
              <span class="tabular-nums">{{ formatMoney(rental.amount_paid_cents) }}</span>
            </div>
            <div
              class="flex justify-between text-sm fw-value"
              :class="rental.balance_due_cents > 0 && !rental.written_off_at ? 'text-[color:var(--danger)]' : 'text-[color:var(--ok)]'"
            >
              <span>{{ t('rental.show.balanceDue') }}</span>
              <span class="tabular-nums">{{ formatMoney(rental.balance_due_cents) }}</span>
            </div>
          </div>
        </UiCard>

        <!-- Payments list -->
        <UiCard :title="t('rental.show.payments')">
          <template #actions>
            <UiButton
              v-if="canAct && ['pending', 'active', 'returned'].includes(rental.status)"
              variant="primary"
              size="sm"
              icon-left="plus"
              @click="openPayment"
            >
              {{ t('rental.show.recordPayment') }}
            </UiButton>
          </template>
          <!-- Desktop table (md+) -->
          <div
            v-if="rental.payments?.length"
            class="hidden md:block"
          >
            <table class="w-full text-sm">
              <thead>
                <tr class="border-b border-[color:var(--line)]">
                  <th
                    scope="col"
                    class="pb-2 text-left text-xs font-medium text-[color:var(--muted)] uppercase"
                  >
                    {{ t('rental.show.columns.type') }}
                  </th>
                  <th
                    scope="col"
                    class="pb-2 text-left text-xs font-medium text-[color:var(--muted)] uppercase"
                  >
                    {{ t('rental.show.columns.method') }}
                  </th>
                  <th
                    scope="col"
                    class="pb-2 text-left text-xs font-medium text-[color:var(--muted)] uppercase"
                  >
                    {{ t('rental.show.columns.date') }}
                  </th>
                  <th
                    scope="col"
                    class="pb-2 text-right text-xs font-medium text-[color:var(--muted)] uppercase"
                  >
                    {{ t('rental.show.columns.amount') }}
                  </th>
                  <th
                    v-if="canAct"
                    scope="col"
                    class="pb-2 w-20"
                  >
                    <span class="sr-only">{{ t('common.edit') }}</span>
                  </th>
                </tr>
              </thead>
              <tbody class="divide-y divide-[color:var(--line)]">
                <tr
                  v-for="p in rental.payments"
                  :key="p.id"
                >
                  <td class="py-2">
                    <PaymentTypePill :type="p.payment_type" />
                  </td>
                  <td class="py-2 text-[color:var(--inkSoft)]">
                    <template v-if="p.payment_type !== 'write_off'">
                      {{ t(`rental.show.paymentModal.methods.${p.payment_method}`) }}
                    </template>
                    <span
                      v-else
                      class="text-xs text-[color:var(--muted)]"
                    >{{ p.recorded_by }}</span>
                  </td>
                  <td class="py-2 text-[color:var(--muted)] text-xs">
                    {{ formatDateTime(p.paid_at) }}
                  </td>
                  <td
                    class="py-2 text-right tabular-nums fw-value"
                    :class="p.payment_type === 'refund' ? 'text-[color:var(--danger)]' : p.payment_type === 'write_off' ? 'text-[color:var(--muted)] line-through' : 'text-[color:var(--ink)]'"
                  >
                    {{ p.payment_type === 'refund' ? '-' : '' }}{{ formatMoney(p.amount_cents) }}
                  </td>
                  <td
                    v-if="canAct"
                    class="py-2 pl-3"
                  >
                    <div
                      v-if="p.payment_type !== 'write_off'"
                      class="flex items-center justify-end gap-1.5"
                    >
                      <button
                        type="button"
                        class="p-1 rounded text-[color:var(--muted)] hover:text-[color:var(--ink)] hover:bg-[color:var(--surfaceAlt)]"
                        :aria-label="t('common.edit')"
                        :title="t('common.edit')"
                        @click="openPaymentEdit(p)"
                      >
                        <Icon
                          name="sign"
                          :size="14"
                        />
                      </button>
                      <button
                        v-if="isAdmin"
                        type="button"
                        class="p-1 rounded text-[color:var(--muted)] hover:text-[color:var(--danger)] hover:bg-[color:var(--surfaceAlt)]"
                        :aria-label="t('common.delete')"
                        :title="t('common.delete')"
                        @click="askDeletePayment(p)"
                      >
                        <Icon
                          name="x"
                          :size="14"
                        />
                      </button>
                    </div>
                  </td>
                </tr>
              </tbody>
            </table>
          </div>

          <!-- Mobile cards (< md) -->
          <div
            v-if="rental.payments?.length"
            class="md:hidden space-y-3"
          >
            <div
              v-for="p in rental.payments"
              :key="p.id"
              class="rounded-[var(--radius-md)] border border-[color:var(--line)] bg-[color:var(--surface)] p-3"
            >
              <div class="flex items-center justify-between">
                <PaymentTypePill :type="p.payment_type" />
                <span
                  class="tabular-nums fw-value text-sm"
                  :class="p.payment_type === 'refund' ? 'text-[color:var(--danger)]' : p.payment_type === 'write_off' ? 'text-[color:var(--muted)] line-through' : 'text-[color:var(--ink)]'"
                >
                  {{ p.payment_type === 'refund' ? '-' : '' }}{{ formatMoney(p.amount_cents) }}
                </span>
              </div>
              <div class="flex items-center justify-between mt-1.5">
                <span class="text-xs text-[color:var(--muted)]">{{ formatDateTime(p.paid_at) }}</span>
                <span class="text-xs text-[color:var(--muted)]">
                  <template v-if="p.payment_type !== 'write_off'">
                    {{ t(`rental.show.paymentModal.methods.${p.payment_method}`) }}
                  </template>
                  <template v-else>{{ p.recorded_by }}</template>
                </span>
              </div>
              <div
                v-if="canAct && p.payment_type !== 'write_off'"
                class="flex items-center gap-2 mt-2 pt-2 border-t border-[color:var(--line)]"
              >
                <button
                  type="button"
                  class="text-xs text-[color:var(--accent)] hover:underline"
                  @click="openPaymentEdit(p)"
                >
                  {{ t('common.edit') }}
                </button>
                <button
                  v-if="isAdmin"
                  type="button"
                  class="text-xs text-[color:var(--danger)] hover:underline"
                  @click="askDeletePayment(p)"
                >
                  {{ t('common.delete') }}
                </button>
              </div>
            </div>
          </div>

          <p
            v-if="!rental.payments?.length"
            class="text-sm text-[color:var(--muted)]"
          >
            {{ t('rental.show.noPayments') }}
          </p>
        </UiCard>

        <!-- History / event log -->
        <UiCard :title="t('rental.show.history')">
          <ol
            v-if="rental.events?.length"
            class="space-y-3"
          >
            <li
              v-for="e in rental.events"
              :key="e.id"
              class="flex gap-3 text-sm"
            >
              <div
                class="mt-1 h-2 w-2 shrink-0 rounded-full"
                :style="{ background: eventDotColor(e.kind) }"
              />
              <div class="min-w-0 flex-1">
                <div class="flex items-baseline justify-between gap-2">
                  <span class="fw-value text-[color:var(--ink)]">
                    {{ eventLabel(e) }}
                  </span>
                  <span class="text-xs text-[color:var(--muted)] tabular-nums shrink-0">
                    {{ formatDateTime(e.created_at) }}
                  </span>
                </div>
                <div
                  v-if="eventDetail(e)"
                  class="text-xs text-[color:var(--inkSoft)] mt-0.5"
                >
                  {{ eventDetail(e) }}
                </div>
                <div
                  v-if="e.user_name"
                  class="text-xs text-[color:var(--muted)] mt-0.5"
                >
                  {{ t('rental.show.by') }} {{ e.user_name }}
                </div>
              </div>
            </li>
          </ol>
          <p
            v-else
            class="text-sm text-[color:var(--muted)]"
          >
            {{ t('rental.show.noEvents') }}
          </p>
        </UiCard>
      </div>

      <!-- Right: 40% -->
      <div class="xl:col-span-2 space-y-4">
        <!-- Delivery section -->
        <UiCard v-if="canAct" title="Delivery">
          <div class="flex flex-col gap-2">
            <DeliveryCard
              v-for="d in deliveries.filter(d => ['pending', 'assigned', 'in_progress'].includes(d.status))"
              :key="d.id"
              :delivery="d"
              :can-cancel="canEditDelivery"
              :can-edit="canEditDelivery"
              :can-reassign="isAdmin"
              @cancel="cancelDelivery"
              @edit="editDelivery"
              @reassign="reassignDelivery"
              @live-map="openLiveMap"
            />

            <UiButton
              v-if="rental.status === 'pending' && !activeDelivery('handover')"
              variant="secondary"
              class="w-full justify-center"
              @click="openDeliveryModal('handover')"
            >
              Send for handover
            </UiButton>

            <UiButton
              v-if="rental.status === 'active' && !activeDelivery('pickup')"
              variant="secondary"
              class="w-full justify-center"
              @click="openDeliveryModal('pickup')"
            >
              Send for pickup
            </UiButton>

            <details
              v-if="deliveries.filter(d => ['completed', 'cancelled'].includes(d.status)).length"
              class="mt-1"
            >
              <summary class="text-sm cursor-pointer text-[color:var(--muted)] py-1">
                History ({{ deliveries.filter(d => ['completed', 'cancelled'].includes(d.status)).length }})
              </summary>
              <DeliveryCard
                v-for="d in deliveries.filter(d => ['completed', 'cancelled'].includes(d.status))"
                :key="d.id"
                :delivery="d"
                :can-cancel="false"
              />
            </details>

            <p
              v-if="!deliveries.length && rental.status !== 'pending' && rental.status !== 'active'"
              class="text-sm text-[color:var(--muted)] text-center py-2"
            >
              No deliveries
            </p>
          </div>
        </UiCard>

        <!-- Quick actions -->
        <UiCard
          v-if="canAct"
          :title="t('rental.show.quickActions')"
        >
          <div class="flex flex-col gap-2">
            <UiButton
              v-if="rental.can_start"
              variant="primary"
              class="w-full justify-center"
              @click="openStart"
            >
              {{ t('rental.show.startRental') }}
            </UiButton>
            <UiButton
              v-if="rental.status === 'pending'"
              variant="secondary"
              icon-left="sign"
              class="w-full justify-center"
              @click="openEditPending"
            >
              {{ t('rental.show.editPending') }}
            </UiButton>
            <div
              v-if="rental.status === 'pending' && !rental.has_contract"
              class="text-xs text-[color:var(--muted)] text-center px-2 py-2 rounded-[var(--radius-md)] bg-[color:var(--surfaceAlt)]"
            >
              {{ t('rental.show.startRentalNeedsContract') }}
            </div>
            <Link
              v-if="rental.status === 'active' && !rental.has_active_pickup_delivery"
              :href="`/rentals/${rental.id}/process_return`"
              replace
              class="block"
            >
              <UiButton
                variant="primary"
                class="w-full justify-center"
              >
                {{ t('rental.show.finishRental') }}
              </UiButton>
            </Link>
            <UiButton
              v-if="rental.can_extend"
              variant="secondary"
              class="w-full justify-center"
              @click="openExtension"
            >
              {{ t('rentals.actions.extend') }}
            </UiButton>
            <UiButton
              v-if="canReplace"
              variant="secondary"
              icon-left="refresh"
              class="w-full justify-center"
              @click="showReplace = true"
            >
              {{ t('rental.show.replaceVehicle') }}
            </UiButton>
            <UiButton
              v-if="rental.status === 'active' && page.props.auth?.user?.role !== 'viewer'"
              variant="secondary"
              icon-left="plus"
              class="w-full justify-center"
              @click="reportDamageOpen = true"
            >
              {{ t('damage.report') }}
            </UiButton>
            <UiButton
              v-if="canCancel"
              variant="danger"
              icon-left="x"
              class="w-full justify-center"
              :disabled="!rental.can_cancel"
              @click="showCancel = true"
            >
              {{ t('rental.show.cancelRental') }}
            </UiButton>
            <UiButton
              v-if="canWriteOff"
              variant="danger"
              icon-left="alert"
              class="w-full justify-center"
              @click="showWriteOff = true"
            >
              {{ t('rental.show.writeOff') }}
            </UiButton>
          </div>
        </UiCard>

        <!-- Open damages on this rental -->
        <UiCard v-if="rental.open_damages?.length">
          <h3 class="text-sm fw-value text-[color:var(--ink)] mb-2">
            {{ t('damage.openOnRental') }}
          </h3>
          <ul class="divide-y divide-[color:var(--line)]">
            <li
              v-for="d in rental.open_damages"
              :key="d.id"
              class="py-2 flex items-center gap-2"
            >
              <UiStatusPill
                :tone="{ minor: 'muted', moderate: 'warn', severe: 'danger' }[d.severity]"
                :label="t(`damage.severity.${d.severity}`)"
              />
              <span class="flex-1 text-sm truncate text-[color:var(--ink)]">{{ d.description }}</span>
              <span class="fw-value tabular-nums text-sm text-[color:var(--ink)]">{{ formatMoney(d.repair_cost_cents) }}</span>
              <Link
                :href="`/vehicles/${d.vehicle_id}`"
                class="text-xs text-[color:var(--accent)] hover:underline"
              >
                View →
              </Link>
            </li>
          </ul>
        </UiCard>

        <!-- Contract (only the entry point — saved versions live in Documents) -->
        <UiCard
          v-if="!rental.saved_contracts?.length"
          title="Contract"
        >
          <p
            v-if="contractBlocked"
            class="text-xs text-[color:var(--muted)] mb-2"
          >
            {{ t('rental.show.contractBlocked') }}
          </p>
          <Link
            v-if="!contractBlocked"
            :href="`/rentals/${rental.id}/contract`"
            class="block"
          >
            <UiButton
              variant="secondary"
              class="w-full justify-center"
            >
              {{ t('rental.show.newContract') }}
            </UiButton>
          </Link>
          <UiButton
            v-else
            variant="secondary"
            class="w-full justify-center"
            disabled
          >
            {{ t('rental.show.newContract') }}
          </UiButton>
        </UiCard>

        <!-- Documents -->
        <UiCard
          v-if="rental.invoice_number || rental.signed || rental.saved_contracts?.length || rental.extensions?.length"
          :title="t('rental.show.documents.title')"
        >
          <div class="space-y-2">
            <!-- Saved contracts -->
            <div
              v-for="c in rental.saved_contracts"
              :key="c.version"
              class="flex items-center justify-between py-2 border-b border-[color:var(--line)] last:border-0"
            >
              <div>
                <p class="text-xs font-medium text-[color:var(--ink)]">
                  Contract v{{ c.version }}
                </p>
                <p class="text-xs text-[color:var(--muted)] mt-0.5">
                  {{ formatDateTime(c.created_at) }}
                </p>
              </div>
              <a
                v-if="c.pdf_url"
                :href="c.pdf_url"
                target="_blank"
              >
                <UiButton
                  variant="secondary"
                  size="sm"
                  icon-left="download"
                >
                  Download
                </UiButton>
              </a>
            </div>

            <!-- Invoice -->
            <div
              v-if="rental.invoice_number"
              class="flex items-center justify-between py-2 border-b border-[color:var(--line)] last:border-0"
            >
              <div>
                <p class="text-xs font-medium text-[color:var(--ink)]">
                  {{ t('rental.show.invoice') }}
                </p>
                <p class="text-xs font-mono text-[color:var(--muted)] mt-0.5">
                  {{ rental.invoice_number }}
                </p>
              </div>
              <Link
                v-if="rental.paid_in_full && rental.invoice_ready"
                :href="`/rentals/${rental.id}/invoice`"
              >
                <UiButton
                  variant="secondary"
                  size="sm"
                  icon-left="download"
                >
                  {{ t('rental.show.downloadInvoice') }}
                </UiButton>
              </Link>
              <span
                v-else-if="rental.paid_in_full"
                class="inline-flex items-center gap-1 text-xs text-[color:var(--muted)] px-2 py-1"
              >
                <Icon
                  name="loader"
                  :size="14"
                />
                {{ t('rental.show.invoiceGenerating') }}
              </span>
              <span
                v-else
                class="inline-flex items-center gap-1 text-xs text-[color:var(--muted)] px-2 py-1"
              >
                <Icon
                  name="alert"
                  :size="14"
                />
                {{ t('rental.show.invoiceLocked') }}
              </span>
            </div>

            <!-- Signed contract -->
            <div
              v-if="rental.signed"
              class="flex items-center justify-between py-2"
            >
              <p class="text-xs font-medium text-[color:var(--ink)]">
                {{ t('rental.show.signedContract') }}
              </p>
              <a
                :href="rental.signed_agreement_path"
                target="_blank"
              >
                <UiButton
                  variant="secondary"
                  size="sm"
                  icon-left="download"
                >
                  {{ t('rental.show.downloadInvoice') }}
                </UiButton>
              </a>
            </div>
          </div>

          <!-- Extensions -->
          <div
            v-if="rental.extensions?.length"
            class="mt-4"
          >
            <div class="text-xs font-semibold uppercase tracking-wider text-[color:var(--muted)] mb-2">
              {{ t('rental.show.documents.extensions') }}
            </div>
            <ul class="space-y-2">
              <li
                v-for="(ext, idx) in rental.extensions"
                :key="ext.id"
                class="flex items-center justify-between gap-3 text-sm"
              >
                <div class="min-w-0">
                  <div class="text-[color:var(--ink)]">
                    {{ t('rental.show.documents.extension_row', {
                      n: rental.extensions.length - idx,
                      date: formatDateTime(ext.new_end_at),
                      days: ext.days_added,
                      amount: ext.total_added
                    }) }}
                  </div>
                  <div
                    v-if="ext.payment_collected_cents > 0"
                    class="text-xs text-[color:var(--muted)]"
                  >
                    {{ t('rental.show.documents.collected', { amount: ext.payment_collected }) }}
                  </div>
                </div>
                <div class="flex items-center gap-2 flex-shrink-0">
                  <a
                    v-if="ext.addendum_path"
                    :href="ext.addendum_path"
                    target="_blank"
                    rel="noopener"
                    class="text-xs underline text-[color:var(--accent)]"
                  >{{ t('rental.show.documents.pdfLabel') }}</a>
                  <a
                    v-if="ext.signed_addendum_path"
                    :href="ext.signed_addendum_path"
                    target="_blank"
                    rel="noopener"
                    class="text-xs underline text-[color:var(--accent)]"
                  >{{ t('rental.show.documents.signedLabel') }}</a>
                  <UiButton
                    v-if="canAct && !ext.signed && ext.addendum_path"
                    variant="secondary"
                    size="sm"
                    @click="openExtensionSign(ext.id)"
                  >
                    {{ t('rental.show.documents.eSign') }}
                  </UiButton>
                  <span
                    v-else-if="!ext.addendum_path"
                    class="text-xs text-[color:var(--muted)]"
                  >{{ t('rental.show.documents.generating') }}</span>
                </div>
              </li>
            </ul>
          </div>
        </UiCard>
      </div>
    </div>

    <!-- Payment modal -->
    <UiModal
      :open="showPayment"
      :title="editingPaymentId ? t('rental.show.paymentModal.editTitle') : t('rental.show.paymentModal.title')"
      size="sm"
      @close="closePayment"
    >
      <div class="space-y-3">
        <div>
          <label
            for="payment-amount"
            class="block text-xs font-medium text-[color:var(--inkSoft)] mb-1"
          >
            {{ t('rental.show.paymentModal.amountLabel') }}
          </label>
          <UiInput
            id="payment-amount"
            v-model="paymentAmount"
            type="number"
            inputmode="decimal"
            placeholder="0"
            :error="paymentForm.errors['payment.amount_cents'] || ''"
          />
        </div>
        <div>
          <label
            for="payment-type"
            class="block text-xs font-medium text-[color:var(--inkSoft)] mb-1"
          >
            {{ t('rental.show.paymentModal.typeLabel') }}
          </label>
          <UiSelect
            id="payment-type"
            v-model="paymentForm.payment.payment_type"
            :options="paymentTypeOptions"
          />
        </div>
        <div v-if="paymentForm.payment.payment_type === 'extra_charge' && rental.open_damages?.length">
          <label
            for="payment-damage"
            class="block text-xs font-medium text-[color:var(--inkSoft)] mb-1"
          >
            {{ t('damage.fields.recoversDamage') }}
          </label>
          <UiSelect
            id="payment-damage"
            v-model="paymentForm.payment.damage_id"
            :options="damageOptions"
          />
        </div>
        <div>
          <label
            for="payment-method"
            class="block text-xs font-medium text-[color:var(--inkSoft)] mb-1"
          >
            {{ t('rental.show.paymentModal.methodLabel') }}
          </label>
          <UiSelect
            id="payment-method"
            v-model="paymentForm.payment.payment_method"
            :options="paymentMethodOptions"
          />
        </div>
        <div>
          <label
            for="payment-date"
            class="block text-xs font-medium text-[color:var(--inkSoft)] mb-1"
          >
            {{ t('rental.show.paymentModal.dateLabel') }}
          </label>
          <UiInput
            id="payment-date"
            v-model="paymentForm.payment.paid_at"
            type="datetime-local"
          />
        </div>
      </div>
      <template #footer>
        <UiButton
          variant="secondary"
          @click="closePayment"
        >
          {{ t('common.cancel') }}
        </UiButton>
        <UiButton
          variant="primary"
          :loading="paymentForm.processing"
          @click="submitPayment"
        >
          {{ t('rental.show.paymentModal.submit') }}
        </UiButton>
      </template>
    </UiModal>

    <!-- Replace-vehicle modal -->
    <UiModal
      :open="showReplace"
      :title="t('rental.show.replaceVehicle')"
      size="sm"
      @close="showReplace = false"
    >
      <div class="space-y-3">
        <p class="text-xs text-[color:var(--muted)]">
          {{ t('rental.show.replaceModal.hint') }}
        </p>
        <div>
          <label
            for="replace-vehicle"
            class="block text-xs font-medium text-[color:var(--inkSoft)] mb-1"
          >
            {{ t('rental.show.replaceModal.label') }}
          </label>
          <UiSelect
            id="replace-vehicle"
            v-model="replaceForm.vehicle_id"
            :options="replace_vehicles.map(v => ({ value: v.id, label: v.label }))"
          />
        </div>
      </div>
      <template #footer>
        <UiButton
          variant="secondary"
          @click="showReplace = false"
        >
          {{ t('common.cancel') }}
        </UiButton>
        <UiButton
          variant="primary"
          :loading="replaceForm.processing"
          :disabled="!replaceForm.vehicle_id"
          @click="submitReplace"
        >
          {{ t('rental.show.replaceModal.submit') }}
        </UiButton>
      </template>
    </UiModal>

    <!-- Cancel confirmation -->
    <ConfirmDialog
      :open="showCancel"
      :title="t('rental.cancel.title')"
      :consequence="cancelConsequence"
      :confirm-text="rental.cancel_refund_cents > 0
        ? t('rental.cancel.confirm', { amount: formatMoney(rental.cancel_refund_cents) })
        : t('rental.cancel.confirmNoRefund')"
      danger
      @confirm="submitCancel"
      @cancel="showCancel = false"
    />

    <!-- Write-off confirmation -->
    <ConfirmDialog
      :open="showWriteOff"
      :title="t('rental.writeOff.title')"
      :consequence="writeOffConsequence"
      :confirm-text="t('rental.writeOff.confirm', { amount: formatMoney(rental.balance_due_cents) })"
      danger
      @confirm="submitWriteOff"
      @cancel="showWriteOff = false"
    />

    <!-- Handover reset confirmation -->
    <ConfirmDialog
      :open="showResetHandover"
      :title="t('rental.show.resetHandoverTitle')"
      :consequence="t('rental.show.resetHandoverConsequence', { address: rental.office_address })"
      :confirm-text="t('rental.show.resetHandover')"
      @confirm="confirmResetHandover"
      @cancel="showResetHandover = false"
    />

    <!-- Signature Modal -->
    <SignatureModal
      v-if="showSignModal"
      :rental-id="rental.id"
      @signed="onSigned"
      @close="showSignModal = false"
    />

    <!-- Extension Modal -->
    <ExtensionModal
      v-if="showExtension"
      :rental="rental"
      :conflict="extensionConflict"
      @close="closeExtension"
    />

    <!-- Extension Signature Modal -->
    <SignatureModal
      v-if="eSignExtensionId"
      :rental-id="rental.id"
      :endpoint="`/rentals/${rental.id}/extensions/${eSignExtensionId}/sign`"
      :title="t('rental.show.extension.signTitle')"
      @signed="onExtensionSigned"
      @close="closeExtensionSign"
    />

    <!-- Handover Point Picker -->
    <LocationPickerModal
      :open="showHandover"
      :title="t('rental.show.handoverModalTitle')"
      :address="rental.handover_address"
      :lat="rental.handover_lat"
      :lng="rental.handover_lng"
      :office-address="rental.office_address"
      :office-lat="rental.office_lat"
      :office-lng="rental.office_lng"
      :saving="handoverSaving"
      @close="showHandover = false"
      @save="onHandoverSave"
    />

    <!-- Return Point Picker -->
    <LocationPickerModal
      :open="showReturn"
      :title="t('rental.show.returnModalTitle')"
      :address="rental.return_address"
      :lat="rental.return_lat"
      :lng="rental.return_lng"
      :office-address="rental.office_address"
      :office-lat="rental.office_lat"
      :office-lng="rental.office_lng"
      :saving="returnSaving"
      @close="showReturn = false"
      @save="onReturnSave"
    />

    <!-- Return reset confirmation -->
    <ConfirmDialog
      :open="showResetReturn"
      :title="t('rental.show.resetReturnTitle')"
      :consequence="t('rental.show.resetHandoverConsequence', { address: rental.office_address })"
      :confirm-text="t('rental.show.resetHandover')"
      @confirm="confirmResetReturn"
      @cancel="showResetReturn = false"
    />

    <!-- Payment delete confirmation -->
    <ConfirmDialog
      :open="!!paymentToDelete"
      :title="t('rental.show.deletePaymentTitle')"
      :consequence="paymentToDelete ? t('rental.show.deletePaymentConsequence', { amount: formatMoney(paymentToDelete.amount_cents) }) : ''"
      :confirm-text="t('common.delete')"
      danger
      @confirm="confirmDeletePayment"
      @cancel="paymentToDelete = null"
    />

    <!-- Edit pending rental -->
    <UiModal
      :open="showEditPending"
      :title="t('rental.show.editPending')"
      size="md"
      @close="showEditPending = false"
    >
      <div class="space-y-3">
        <div class="grid grid-cols-2 gap-3">
          <div>
            <label class="block text-xs font-medium text-[color:var(--inkSoft)] mb-1">
              {{ t('rental.create.startDate') }}
            </label>
            <UiInput
              v-model="editPendingForm.rental.start_at"
              type="date"
            />
          </div>
          <div>
            <label class="block text-xs font-medium text-[color:var(--inkSoft)] mb-1">
              {{ t('rental.create.expectedEnd') }}
            </label>
            <UiInput
              v-model="editPendingForm.rental.expected_end_at"
              type="date"
            />
          </div>
        </div>
        <div class="grid grid-cols-2 gap-3">
          <div>
            <label class="block text-xs font-medium text-[color:var(--inkSoft)] mb-1">
              {{ t('rental.show.fields.dailyRate') }}
            </label>
            <UiInput
              v-model="editPendingForm.rental.daily_rate"
              type="number"
              inputmode="decimal"
              placeholder="0"
            />
          </div>
          <div>
            <label class="block text-xs font-medium text-[color:var(--inkSoft)] mb-1">
              {{ t('rental.show.fields.depositPaid') }}
            </label>
            <UiInput
              v-model="editPendingForm.rental.deposit"
              type="number"
              inputmode="decimal"
              placeholder="0"
            />
          </div>
        </div>
        <div>
          <label class="block text-xs font-medium text-[color:var(--inkSoft)] mb-1">
            {{ t('rental.show.fields.notes') }}
          </label>
          <textarea
            v-model="editPendingForm.rental.notes"
            rows="3"
            class="w-full px-3 py-2 rounded-[var(--radius-md)] border border-[color:var(--line)] bg-[color:var(--surface)] text-[color:var(--ink)] text-sm focus:outline-2 focus:outline-[color:var(--accent)]"
          />
        </div>
      </div>
      <template #footer>
        <UiButton
          variant="secondary"
          @click="showEditPending = false"
        >
          {{ t('common.cancel') }}
        </UiButton>
        <UiButton
          variant="primary"
          @click="submitEditPending"
        >
          {{ t('common.save') }}
        </UiButton>
      </template>
    </UiModal>

    <!-- Start Rental Modal -->
    <UiModal
      :open="showStart"
      :title="t('rental.start.title')"
      size="sm"
      @close="showStart = false"
    >
      <div class="space-y-3">
        <label class="flex items-center gap-2 text-sm text-[color:var(--ink)] cursor-pointer">
          <input
            v-model="startForm.as_planned"
            type="checkbox"
            class="accent-[color:var(--accent)]"
          >
          {{ t('rental.start.asPlanned') }}
        </label>
        <div>
          <label class="block text-xs font-medium text-[color:var(--inkSoft)] mb-1">
            {{ t('rental.show.fields.start') }}
          </label>
          <UiInput
            v-model="startForm.rental.start_at"
            type="datetime-local"
            :disabled="startForm.as_planned"
          />
        </div>
        <div>
          <label class="block text-xs font-medium text-[color:var(--inkSoft)] mb-1">
            {{ t('rental.show.fields.expectedReturn') }}
          </label>
          <UiInput
            v-model="startForm.rental.expected_end_at"
            type="datetime-local"
            :disabled="startForm.as_planned"
          />
        </div>
        <p class="text-xs text-[color:var(--muted)]">
          {{ t('rental.start.hint') }}
        </p>
      </div>
      <template #footer>
        <UiButton
          variant="secondary"
          @click="showStart = false"
        >
          {{ t('common.cancel') }}
        </UiButton>
        <UiButton
          variant="primary"
          @click="submitStart"
        >
          {{ t('rental.show.startRental') }}
        </UiButton>
      </template>
    </UiModal>

    <!-- Report Damage Modal -->
    <ReportDamageModal
      :open="reportDamageOpen"
      :vehicle-id="rental.vehicle_id"
      :rental-id="rental.id"
      @close="reportDamageOpen = false"
    />

    <SendToDeliveryModal
      v-if="showDeliveryModal"
      :rental="rental"
      :kind="deliveryKind"
      :available-drivers="available_drivers"
      @close="showDeliveryModal = false"
    />

    <EditDeliveryModal
      v-if="editingDelivery"
      :delivery="editingDelivery"
      @close="editingDelivery = null"
    />

    <ReassignDriverModal
      v-if="reassigningDelivery"
      :delivery="reassigningDelivery"
      :available-drivers="available_drivers"
      @close="reassigningDelivery = null"
    />

    <div
      v-if="liveMapDelivery"
      class="fixed inset-0 z-50 flex md:items-center md:justify-center md:p-4 bg-black/60"
      @click.self="closeLiveMap"
    >
      <div class="w-full h-full md:h-auto md:max-w-3xl md:rounded-[var(--radius-lg)] md:shadow-[var(--shadow-lg)] bg-[color:var(--surface)] flex flex-col md:max-h-[calc(100vh-2rem)]">
        <header class="flex-shrink-0 flex items-center justify-between p-4 border-b border-[color:var(--line)]">
          <h2 class="text-base font-semibold text-[color:var(--ink)]">
            {{ liveMapDelivery.kind === 'handover' ? 'Handover' : 'Pickup' }} · driver location
          </h2>
          <button
            class="text-xl leading-none text-[color:var(--muted)] hover:text-[color:var(--ink)]"
            type="button"
            @click="closeLiveMap"
          >
            ×
          </button>
        </header>
        <div class="flex-1 overflow-hidden p-3 min-h-[60vh] md:min-h-[420px]">
          <LiveLocationMap
            :delivery-id="liveMapDelivery.id"
            :destination="{
              lat: liveMapDelivery.lat,
              lng: liveMapDelivery.lng,
              label: liveMapDelivery.address,
            }"
          />
        </div>
      </div>
    </div>
  </PageShell>
</template>
